import { useEffect, useState, RefObject, ReactElement } from 'react';
import { renderToString } from '../../../stubs/react-dom-server-client';
import { sanitizePlainText } from '../../../libs/sanitizeContent';
import { RioSelectGroup, RioSelectOption } from './types';
import { SelectInstance } from 'react-select';
import { useIsTouchDevice } from '../../grid/hooks';

export function isGroup<T>(option: RioSelectOption<T>): option is RioSelectGroup<T> {
  return typeof option === 'object' && option !== null && 'options' in option;
}

export function identity<T>(value: T): T {
  return value;
}

export function normalizeOption<T>(option: T | string): T {
  return typeof option !== 'object' || option === null
    ? ({
        label: option,
        value: option,
        _isNormalized: true
      } as T)
    : option;
}

export const getValueFromReactSelectOption = (option: any) => {
  if (typeof option === 'object' && option !== null && ('_isNormalized' in option || '__isNew__' in option)) {
    return option.value;
  }

  return option;
};

export const getOptionFromReactSelectOption = (option: any) => {
  if (typeof option === 'object' && option !== null && ('_isNormalized' in option || '__isNew__' in option)) {
    return option;
  }

  return option;
};

interface IUseCustomReactMenuTriggerV2Props {
  selectRef?: RefObject<SelectInstance | null>;
  initialMenuIsOpen?: boolean;
  closeMenuOnSelect: boolean;
  disabled?: boolean;
}

/**
 * React-select's menu opening logic is based on input focus/blur events.
 * To use custom inputs inside the menu, we need to manually manage the menu's opening and closing logic.
 */
export const useCustomReactMenuTrigger = ({
  selectRef,
  initialMenuIsOpen = false,
  closeMenuOnSelect,
  disabled
}: IUseCustomReactMenuTriggerV2Props) => {
  const [menuIsOpen, setMenuIsOpen] = useState(initialMenuIsOpen);
  const isTouchDevice = useIsTouchDevice();

  useEffect(() => {
    const { controlRef } = selectRef?.current || {};
    if (!controlRef || disabled) {
      return;
    }

    const rootElement = controlRef.closest('.rio-select');
    // Custom inputs can be siblings of MenuList inside a portaled menu.
    const isInsideSelect = (target: Node) =>
      rootElement?.contains(target) || selectRef?.current?.menuListRef?.closest('.rio-select__menu')?.contains(target);

    let userIsDragging = false;
    let initialTouchX: number = 0;
    let initialTouchY: number = 0;

    const onControlElementClick = (e: any) => {
      const multiValueRemoveClass = 'rio-select__multi-value__remove';
      const clearIndicatorClass = 'rio-select__clear-indicator';

      if (e.target.closest(`.${multiValueRemoveClass}`) || e.target.closest(`.${clearIndicatorClass}`)) {
        return;
      }

      setMenuIsOpen((prev) => !prev);
    };

    const onControlTouchEnd = () => {
      if (userIsDragging) {
        return;
      }

      setMenuIsOpen((prev) => !prev);
    };

    const onDocumentClick: EventListener = (e) => {
      const target = e.target as any;

      if (!isInsideSelect(target)) {
        setMenuIsOpen(false);
      }
    };

    const onTouchStart = ({ touches }: TouchEvent) => {
      const touch = touches && touches.item(0);
      if (!touch) {
        return;
      }

      initialTouchX = touch.clientX;
      initialTouchY = touch.clientY;
      userIsDragging = false;
    };

    const onTouchMove = ({ touches }: TouchEvent) => {
      const touch = touches && touches.item(0);
      if (!touch) {
        return;
      }

      const deltaX = Math.abs(touch.clientX - initialTouchX);
      const deltaY = Math.abs(touch.clientY - initialTouchY);
      const moveThreshold = 5;

      userIsDragging = deltaX > moveThreshold || deltaY > moveThreshold;
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (userIsDragging) {
        return;
      }

      const target = event.target as any;

      if (!isInsideSelect(target)) {
        setMenuIsOpen(false);
      }

      initialTouchX = 0;
      initialTouchY = 0;
    };

    const handleCloseMenu = () => {
      setMenuIsOpen(false);
    };

    if (isTouchDevice) {
      controlRef.addEventListener('touchend', onControlTouchEnd);
      document.addEventListener('touchend', onTouchEnd);
      document.addEventListener('touchmove', onTouchMove);
      document.addEventListener('touchstart', onTouchStart);
    } else {
      controlRef.addEventListener('mousedown', onControlElementClick);
      if (menuIsOpen) {
        document.addEventListener('click', onDocumentClick);
      }
    }

    if (menuIsOpen) {
      window.addEventListener('blur', handleCloseMenu);
      window.addEventListener('clear-indicator-clicked', handleCloseMenu);
    }

    return () => {
      controlRef.removeEventListener('touchend', onControlTouchEnd);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchstart', onTouchStart);
      controlRef.removeEventListener('mousedown', onControlElementClick);
      document.removeEventListener('click', onDocumentClick);
      window.removeEventListener('blur', handleCloseMenu);
      window.removeEventListener('clear-indicator-clicked', handleCloseMenu);
    };
  }, [selectRef, menuIsOpen, closeMenuOnSelect, disabled, isTouchDevice]);

  return [menuIsOpen, setMenuIsOpen] as const;
};

export const sortOptions = <Option>(
  options: RioSelectOption<Option>[],
  sortFunction?: (a: Option, b: Option) => number
): RioSelectOption<Option>[] => {
  const sortedList = sortFunction ? options.concat().sort(sortFunction as any) : options;

  return sortedList.map<RioSelectOption<Option>>((option) => {
    if (isGroup(option)) {
      return {
        ...option,
        options: sortOptions(option.options, sortFunction) as Option[]
      };
    }

    return option;
  });
};

export const extractTextFromReactNode = (node: ReactElement): string => {
  return sanitizePlainText(renderToString(node));
};
